import { useMemo } from 'react';
import type { WorkflowFormState, WorkflowBuilderStep } from '../pages/admin/WorkflowBuilderTypes.js';

export interface FieldChange {
  previous: unknown;
  current: unknown;
  label: string;
}

// ==========================================
// Deep equality (mirrors useWorkflowForm.ts)
// ==========================================

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (typeof a === 'object') {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

// ==========================================
// Field definitions
// ==========================================

const TOP_LEVEL_FIELDS: Array<{ key: keyof WorkflowFormState; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'category', label: 'Category' },
  { key: 'status', label: 'Status' },
  { key: 'tags', label: 'Tags' },
  { key: 'cronSchedule', label: 'Cron Schedule' },
  { key: 'scheduleEnabled', label: 'Schedule Enabled' },
  { key: 'flowDirection', label: 'Flow Direction' },
];

const STEP_FIELDS: Array<{ key: keyof WorkflowBuilderStep; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'templateSlug', label: 'Agent Template' },
  { key: 'templateId', label: 'Template ID' },
  { key: 'executionMode', label: 'Execution Mode' },
  { key: 'optional', label: 'Optional' },
  { key: 'parallelGroup', label: 'Parallel Group' },
  { key: 'order', label: 'Order' },
  { key: 'systemPromptOverride', label: 'System Prompt Override' },
  { key: 'additionalInstructions', label: 'Additional Instructions' },
  { key: 'userPromptFreetext', label: 'User Prompt (Freetext)' },
  { key: 'promptMode', label: 'Prompt Mode' },
  { key: 'outputPortType', label: 'Output Port Type' },
  { key: 'toolSelectionMode', label: 'Tool Selection Mode' },
  { key: 'approvalStatus', label: 'Approval Status' },
  { key: 'contractNegotiationStatus', label: 'Contract Status' },
];

// ==========================================
// Hook
// ==========================================

export function useFieldChangeTracker(
  current: WorkflowFormState,
  saved: WorkflowFormState | null,
): Map<string, FieldChange> {
  return useMemo(() => {
    const changes = new Map<string, FieldChange>();
    if (!saved) return changes;

    // Top-level fields
    for (const { key, label } of TOP_LEVEL_FIELDS) {
      if (!deepEqual(current[key], saved[key])) {
        changes.set(key, { previous: saved[key], current: current[key], label });
      }
    }

    // Step-level fields
    const savedStepMap = new Map(saved.steps.map((s) => [s.id.toString(), s]));
    const currentStepMap = new Map(current.steps.map((s) => [s.id.toString(), s]));

    // Current steps: added or changed
    for (const step of current.steps) {
      const stepKey = step.id.toString();
      const savedStep = savedStepMap.get(stepKey);
      if (!savedStep) {
        // Entire step is new — mark each field as added
        for (const { key, label } of STEP_FIELDS) {
          changes.set(`steps.${stepKey}.${key}`, {
            previous: null,
            current: step[key],
            label,
          });
        }
      } else {
        // Compare individual fields
        for (const { key, label } of STEP_FIELDS) {
          if (!deepEqual(step[key], savedStep[key])) {
            changes.set(`steps.${stepKey}.${key}`, {
              previous: savedStep[key],
              current: step[key],
              label,
            });
          }
        }
      }
    }

    // Removed steps
    for (const step of saved.steps) {
      if (!currentStepMap.has(step.id.toString())) {
        const stepKey = step.id.toString();
        for (const { key, label } of STEP_FIELDS) {
          changes.set(`steps.${stepKey}.${key}`, {
            previous: step[key],
            current: null,
            label,
          });
        }
      }
    }

    return changes;
  }, [current, saved]);
}
